/**
 * Rybbit event payload helpers for LiveFlight.
 *
 * Rybbit's /api/track endpoint enforces a 2048 character limit on the
 * `properties` field. These helpers produce slim payloads that stay
 * well under the limit while keeping every field that matters for analytics.
 */

/**
 * Slim a full seat object down to analytics-relevant fields.
 *
 * Drops: travelerPricing array, coordinates, left/right neighbors,
 *        isPartiallyAvailable, currentTravelerChoosing, etc.
 */
export const slim_seat = (seat) => {
  if (!seat) {
    return;
  }
  const { travelerPricing = [] } = seat;
  if (travelerPricing.length === 0) {
    return;
  }
  const { price: pricing = {} } = travelerPricing[0];
  return {
    number: seat.number,
    price: pricing.grandTotal || pricing.total,
  };
};

/**
 * Slim selections map from { segmentId: { travelerId: fullSeatObject } }
 * to { segmentId: { travelerId: seatNumber } }.
 */
export const slim_selections = (selections) => {
  if (!selections) {
    return {};
  }
  return Object.keys(selections).reduce((acc, seg_id) => {
    acc[seg_id] = Object.keys(selections[seg_id] || {}).reduce(
      (seg_acc, t_id) => {
        const { number: seat } = selections[seg_id][t_id] || {};
        seg_acc[t_id] = seat;
        return seg_acc;
      },
      {},
    );
    return acc;
  }, {});
};

/**
 * Slim a traveler object to analytics-safe fields (no PII like birth/gender).
 */
export const slim_traveler = (traveler) => {
  if (!traveler) return undefined;
  return {
    id: traveler.id,
    traveler_type: traveler.traveler_type || traveler.travelerType,
  };
};

/**
 * Extract a slim flight-offer summary for analytics.
 *
 * Previously lived in flight-offer.js as get_flight_offer_rybbit_data.
 */
export const slim_flight_offer = (flight_offer) => {
  if (!flight_offer) return {};

  const itineraries = (flight_offer.itineraries || []).map((itinerary) => {
    const segments = itinerary.segments || [];
    const first_segment = segments[0];
    const last_segment = segments[segments.length - 1];

    return {
      departure_airport:
        first_segment &&
        first_segment.departure &&
        first_segment.departure.iataCode,
      arrival_airport:
        last_segment && last_segment.arrival && last_segment.arrival.iataCode,
      departure_date:
        first_segment && first_segment.departure && first_segment.departure.at,
      arrival_date:
        last_segment && last_segment.arrival && last_segment.arrival.at,
      stops: segments.length - 1,
      carrier: first_segment && first_segment.carrierCode,
    };
  });

  const traveler_pricings = flight_offer.travelerPricings || [];
  const first_fare =
    traveler_pricings[0] &&
    traveler_pricings[0].fareDetailsBySegment &&
    traveler_pricings[0].fareDetailsBySegment[0];

  return {
    source: flight_offer.source || "",
    branded_fare: (first_fare && first_fare.brandedFare) || "",
    itineraries,
  };
};
